<template>
  <main class="VesselAssessment">
    <section
        v-if="step === 'details'"
        class="VesselAssessment-panel VesselAssessment-panel--details">
      <header class="VesselAssessment-intro">
        <h1 class="VesselAssessment-title">Get a vessel-specific assessment of the<br class="VesselAssessment-titleBreak"> impact of hull coating</h1>
        <p class="VesselAssessment-copy">
          Fill in the non-obligatory questionnaire for your Superyacht and we will send you potential savings calculation and the return of investment analysis.
        </p>
      </header>

      <form
          class="VesselAssessment-form"
          novalidate
          @submit.prevent="goToConfirmation">
        <div
            v-for="field in measurementFields"
            :key="field.name"
            class="VesselAssessment-row VesselAssessment-row--measurement">
          <label
              class="VesselAssessment-label"
              :for="field.name">{{ field.label }}:</label>
          <div class="VesselAssessment-control VesselAssessment-control--withUnit">
            <input
                class="VesselAssessment-input"
                :class="{ 'VesselAssessment-input--error': hasError(field.name) }"
                :id="field.name"
                :name="field.name"
                v-model="form[field.name]"
                type="number"
                inputmode="decimal"
                min="0"
                step="any"
                @input="clearError(field.name)">
            <span class="VesselAssessment-unit">{{ field.unit }}</span>
          </div>
        </div>

        <div
            class="VesselAssessment-row VesselAssessment-row--radio"
            :class="{ 'VesselAssessment-row--error': hasError('engineType') }"
            role="radiogroup"
            aria-labelledby="engineTypeLabel">
          <div
              id="engineTypeLabel"
              class="VesselAssessment-label">Engine type:</div>
          <div class="VesselAssessment-radioGroup">
            <label class="VesselAssessment-radioLabel">
              <input
                  class="VesselAssessment-radio"
                  type="radio"
                  name="engineType"
                  value="II"
                  v-model="form.engineType"
                  @change="clearError('engineType')">
              <span>II</span>
            </label>
            <label class="VesselAssessment-radioLabel">
              <input
                  class="VesselAssessment-radio"
                  type="radio"
                  name="engineType"
                  value="III"
                  v-model="form.engineType"
                  @change="clearError('engineType')">
              <span>III</span>
            </label>
          </div>
        </div>

        <div
            v-for="field in vesselFields"
            :key="field.name"
            class="VesselAssessment-row VesselAssessment-row--text">
          <label
              class="VesselAssessment-label"
              :for="field.name">
            {{ field.label }}:
            <small
                v-if="field.note"
                class="VesselAssessment-labelNote">{{ field.note }}</small>
          </label>
          <div class="VesselAssessment-control">
            <input
                class="VesselAssessment-input"
                :class="{ 'VesselAssessment-input--error': hasError(field.name) }"
                :id="field.name"
                :name="field.name"
                v-model="form[field.name]"
                type="text"
                @input="clearError(field.name)">
          </div>
        </div>

        <p
            v-if="formError"
            class="VesselAssessment-errorMessage"
            role="alert">{{ formError }}</p>

        <div class="VesselAssessment-actions">
          <button
              class="VesselAssessment-button"
              type="submit">
            <span>Submit</span>
            <span class="VesselAssessment-buttonIcon" aria-hidden="true">›</span>
          </button>
        </div>
      </form>
    </section>

    <section
        v-else
        class="VesselAssessment-panel VesselAssessment-panel--confirmation">
      <header class="VesselAssessment-intro VesselAssessment-intro--confirmation">
        <h1 class="VesselAssessment-title">Thank you!</h1>
        <p class="VesselAssessment-copy VesselAssessment-copy--confirmation">
          You will receive your personalised calculation and presentation soon.
        </p>
      </header>

      <form
          class="VesselAssessment-confirmationForm"
          novalidate
          @submit.prevent="submitAssessment">
        <div class="VesselAssessment-emailRow">
          <label
              class="VesselAssessment-label VesselAssessment-label--email"
              for="assessmentEmail">Please confirm the e-mail address:</label>
          <input
              class="VesselAssessment-input VesselAssessment-input--email"
              :class="{ 'VesselAssessment-input--error': hasError('email') }"
              id="assessmentEmail"
              name="email"
              v-model="email"
              type="email"
              @input="clearError('email')">
        </div>

        <div class="VesselAssessment-consent">
          <p class="VesselAssessment-consentTitle">Stay connected with Hempel!</p>
          <p class="VesselAssessment-consentCopy">
            By clicking "I agree", you're giving us permission to share relevant updates about
            our products and services with you via email, phone or similar from Hempel and its
            affiliated companies.
          </p>

          <label class="VesselAssessment-checkboxLabel">
            <input
                class="VesselAssessment-checkbox"
                type="checkbox"
                v-model="marketingConsent">
            <span>I agree</span>
          </label>

          <p class="VesselAssessment-privacyCopy">
            Upon submission, I acknowledge and agree that my personal data will be handled by
            the Hempel entity specified for my country here. To know more about how we handle
            your personal data, see our
            <a
                href="https://www.hempel.com/privacy-notice"
                target="_blank"
                rel="noopener noreferrer">Privacy Notice</a>.
          </p>
          <p class="VesselAssessment-privacyCopy VesselAssessment-privacyCopy--unsubscribe">
            You can unsubscribe at any time using the link provided in our emails.
          </p>
        </div>

        <p
            v-if="formError"
            class="VesselAssessment-errorMessage VesselAssessment-errorMessage--confirmation"
            role="alert">{{ formError }}</p>

        <div class="VesselAssessment-actions VesselAssessment-actions--confirmation">
          <transition name="VesselAssessment-fade" mode="out-in">
            <button
                v-if="!isSubmitted"
                key="submit-button"
                class="VesselAssessment-button"
                type="submit"
                :disabled="isSubmitting">
              <span>{{ isSubmitting ? 'Sending' : 'Confirm' }}</span>
              <span class="VesselAssessment-buttonIcon" aria-hidden="true">✓</span>
            </button>

            <p
                v-else
                key="submit-success"
                class="VesselAssessment-successMessage"
                role="status"
                aria-live="polite">
              Your request has been received. We will contact you soon.
            </p>
          </transition>
        </div>
      </form>
    </section>
  </main>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VesselSpecificAssessment',
  data() {
    return {
      step: 'details',
      form: {
        underwaterSurfaceArea: '',
        range: '',
        cruiseSpeed: '',
        tankSize: '',
        engineType: '',
        superyachtName: '',
        imoNumber: '',
        activityLevel: ''
      },
      email: '',
      marketingConsent: false,
      errors: {},
      formError: '',
      isSubmitting: false,
      isSubmitted: false,
      measurementFields: [
        {
          name: 'underwaterSurfaceArea',
          label: 'Underwater surface area',
          unit: 'm2'
        },
        {
          name: 'range',
          label: 'Range',
          unit: 'nm'
        },
        {
          name: 'cruiseSpeed',
          label: 'Cruise speed',
          unit: 'knots'
        },
        {
          name: 'tankSize',
          label: 'Tank size',
          unit: 'litres'
        }
      ],
      vesselFields: [
        {
          name: 'superyachtName',
          label: 'Superyacht name'
        },
        {
          name: 'imoNumber',
          label: 'IMO number'
        },
        {
          name: 'activityLevel',
          label: 'Activity level',
          note: '(in case IMO number doesnt exist)'
        }
      ]
    }
  },
  methods: {
    hasError(field) {
      return !!this.errors[field]
    },
    clearError(field) {
      if (!this.errors[field]) {
        return
      }

      this.$delete(this.errors, field)

      if (!Object.keys(this.errors).length) {
        this.formError = ''
      }
    },
    goToConfirmation() {
      this.errors = {}
      this.formError = ''

      const requiredFields = [
        'underwaterSurfaceArea',
        'range',
        'cruiseSpeed',
        'tankSize',
        'engineType',
        'superyachtName'
      ]

      requiredFields.forEach(field => {
        if (!String(this.form[field]).trim()) {
          this.$set(this.errors, field, true)
        }
      })

      if (!String(this.form.imoNumber).trim() && !String(this.form.activityLevel).trim()) {
        this.$set(this.errors, 'imoNumber', true)
        this.$set(this.errors, 'activityLevel', true)
      }

      if (Object.keys(this.errors).length) {
        this.formError = 'Please complete all required fields. Add either an IMO number or an activity level.'
        return
      }

      this.step = 'confirmation'

      this.$nextTick(() => {
        window.scrollTo(0, 0)

        const emailInput = document.getElementById('assessmentEmail')

        if (emailInput) {
          emailInput.focus()
        }
      })
    },
    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    },
    emailEndpoint() {
      return process.env.VUE_APP_VESSEL_ASSESSMENT_ENDPOINT || '/send'
    },
    assessmentLink() {
      return `${window.location.origin}${this.$route.fullPath}`
    },
    submitAssessment() {
      if (this.isSubmitting || this.isSubmitted) {
        return
      }

      this.errors = {}
      this.formError = ''

      if (!this.isValidEmail(this.email)) {
        this.$set(this.errors, 'email', true)
        this.formError = 'Please enter a valid e-mail address.'
        return
      }

      this.isSubmitting = true

      const payload = {
        type: 'vesselSpecificAssessment',
        email: this.email,
        link: this.assessmentLink(),
        marketingConsent: this.marketingConsent,
        data: this.form
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('[Vessel assessment] endpoint:', this.emailEndpoint())
        console.log('[Vessel assessment] data:', payload)
        console.table(this.form)
      }

      axios
          .post(this.emailEndpoint(), payload)
          .then(() => {
            this.formError = ''
            this.isSubmitted = true

            this.dataLayerAdd({
              type: 'calculatorEvent',
              category: 'Vessel Specific Assessment',
              action: 'Submit questionnaire',
              label: this.email
            })
          })
          .catch(error => {
            console.error(
              '[Vessel assessment] send failed:',
              error.response && error.response.status,
              error.response && error.response.data,
              error
            )

            this.formError = 'Something went wrong while sending the form. Please try again.'
          })
          .then(() => {
            this.isSubmitting = false
          })
    }
  }
}
</script>